import React, { useCallback, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { TFunction } from "i18next";
import { MdCheck, MdStarBorder } from "react-icons/md";
import HtmlEditor, { HtmlEditorRef } from "../../../../atoms/HtmlEditor";
import {
  RICH_TEXT_LIMITS,
  richTextProblem,
} from "../../../../../utils/richText";
import { CustomButton } from "../../../../atoms/CustomButton";
import { StatusBadge } from "../../../../atoms/StatusBadge";
import { SendMessageFormValues } from "./types";
import { isHtmlEmpty, normalizeSignatureHtml } from "./helpers";

type SignatureSectionProps = {
  t: TFunction;
  signatureEditorRef: React.MutableRefObject<HtmlEditorRef | null>;
  savedDefaultSignature?: string;
  onSaveDefault?: () => void;
};

/** The section is folded by default: a signature over the limit holds the message back from there too. */
export const SignatureLimitNotice: React.FC<{
  t: TFunction;
  signature?: string | null;
}> = ({ t, signature }) => {
  const problem = richTextProblem(signature, RICH_TEXT_LIMITS.messageSignature);
  if (!problem) return null;
  return (
    <p className="pb-4 text-sm font-medium text-red-600" role="alert">
      {problem === "tooLong"
        ? t("htmlEditor.tooLongMax", {
            max: RICH_TEXT_LIMITS.messageSignature,
          })
        : t("htmlEditor.tooHeavy")}
    </p>
  );
};

const SignatureSection: React.FC<SignatureSectionProps> = ({
  t,
  signatureEditorRef,
  savedDefaultSignature,
  onSaveDefault,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<SendMessageFormValues>();
  const [editorProblem, setEditorProblem] = useState<string>();
  const handleEditorValidation = useCallback(
    (_isValid: boolean, message?: string) => setEditorProblem(message),
    []
  );
  const signatureProblem = editorProblem ?? errors.signature?.message;
  const currentSignature = useWatch({ control, name: "signature" });
  const normalizedDefault = normalizeSignatureHtml(savedDefaultSignature);
  const isDefault =
    normalizedDefault !== "" &&
    normalizeSignatureHtml(currentSignature) === normalizedDefault;

  return (
    <section>
      <p className="mb-3 text-xs text-gray-500">
        {t("dashboard.entities.viewEntityUser.sendMsg.helperText.signatureTip")}
      </p>

      <div
        translate="no"
        className="rounded-lg border border-solid border-gray-300 bg-white"
      >
        <Controller
          name="signature"
          control={control}
          defaultValue=""
          rules={{
            validate: (value) => !isHtmlEmpty(value || "") || true,
          }}
          render={({ field: { onChange, value } }) => (
            <HtmlEditor
              ref={signatureEditorRef}
              value={value}
              onChange={(content) => {
                const empty = isHtmlEmpty(content || "");
                onChange(empty ? "" : content);
              }}
              placeholder={
                t(
                  "dashboard.entities.viewEntityUser.sendMsg.placeholders.signaturePlaceholder"
                ) as string
              }
              minHeight="150px"
              maxHeight="300px"
              className="border-gray-300"
              maxLength={RICH_TEXT_LIMITS.messageSignature}
              onValidationChange={handleEditorValidation}
              showCharacterCount={true}
            />
          )}
        />
      </div>

      {signatureProblem && (
        <p className="mt-2 text-sm font-medium text-red-600">
          {signatureProblem}
        </p>
      )}

      <p className="mt-3 text-xs text-gray-500">
        <strong className="font-medium">
          {t("dashboard.entities.viewEntityUser.sendMsg.helperText.examples")}
        </strong>{" "}
        {t(
          "dashboard.entities.viewEntityUser.sendMsg.helperText.signatureExamples"
        )}
      </p>

      {isDefault ? (
        <div className="mt-2 flex items-center justify-end">
          <StatusBadge color="gray" icon={MdCheck}>
            {t("dashboard.entities.viewEntityUser.sendMsg.isYourDefault")}
          </StatusBadge>
        </div>
      ) : onSaveDefault ? (
        <div className="mt-2 flex items-center justify-end">
          <CustomButton
            variant="none"
            size="xs"
            onClick={onSaveDefault}
            className="gap-1 text-gray-500 enabled:hover:text-blue-700"
            icon={<MdStarBorder size={14} />}
            title={
              t(
                "dashboard.entities.viewEntityUser.sendMsg.setAsDefault"
              ) as string
            }
          />
        </div>
      ) : null}
    </section>
  );
};

export default SignatureSection;
